import { useEffect, useRef, useState } from 'react';
import { GLOBAL_DATA_CDN_URL } from 'utils/urls';

import { ActivityMetrics } from 'components/SaloonLobby/types';

/**
 * Reads the platform-wide activity strip (Tables / Players Seated / In Play)
 * from its own public CDN document instead of the legacy engine's
 * `lobby:v1:activity:response` WebSocket event — see
 * serverless-v2/services/lobby-broadcast/functions/broadcastActivityStrip.
 * That socket listener (pages/Lobby/Lobby.tsx, GuestLobby.tsx) has been
 * dormant since the legacy broadcaster was removed; this hook is the new,
 * independent source. A guest browsing the lobby needs no socket connection
 * at all for this metric.
 *
 * Deliberately its own small poller rather than the shared useCdnDocument
 * hook (used by other CDN readers on the larger CDN-migration branch): this
 * feature's own bucket/distribution/URL is independent of that work, and
 * duplicating ~40 lines of polling logic here avoids coupling this reader's
 * interface to a hook this branch doesn't otherwise depend on.
 *
 * Poll interval matches ACTIVITY_STRIP_TICK_INTERVAL_MS (shared/lib/cadence.js)
 * and the document's own Cache-Control max-age (activityStripDocument.js) —
 * default 5s, overridable per-build via REACT_APP_ACTIVITY_STRIP_POLL_MS.
 * The document's freshness/staleness thresholds are NOT tied to this poll
 * cadence: they're derived at read time from the document's own published
 * `maxAgeMs` field (see isDocumentFresh below), so widening
 * ACTIVITY_STRIP_TICK_INTERVAL_MS on the publisher takes effect for every
 * client on its very next successful read — no client rebuild needed. Only
 * the poll cadence itself (REACT_APP_ACTIVITY_STRIP_POLL_MS) is a client-side
 * env var, and widening it alone doesn't require a publisher change.
 */

// The largest delay `setTimeout` honors as configured: browsers and Node
// both store a timer's delay in a 32-bit signed int (the HTML/WHATWG timer
// spec), so anything above 2^31-1 (~24.8 days) silently clamps to fire
// near-immediately instead of throwing or waiting the full requested delay.
const MAX_TIMER_MS = 2_147_483_647;

// Halved, not the raw ceiling: REQUEST_TIMEOUT_MS below is 2x POLL_MS, and a
// POLL_MS just under MAX_TIMER_MS would double past it — silently overflowing
// into the exact near-immediate-fire failure mode this cap exists to prevent.
// computeBackoffMs's own Math.min already keeps its return from exceeding
// Math.max(MAX_BACKOFF_MS, pollMs), so bounding pollMs itself here is enough
// to keep every downstream setTimeout call (the request timeout, the healthy
// poll, and the backoff) within the safe range — no separate cap needed there.
const MAX_POLL_MS = Math.floor(MAX_TIMER_MS / 2);

// Guarded the same way cadence.js's readActivityStripTickIntervalMs() and
// activityStripCacheTtl.cjs's readTickIntervalMs() are: a negative value is
// truthy in JS, so `|| 5000` alone would let a malformed env var (e.g. a
// fat-fingered "-5000") through unnoticed. Unlike those two, nothing downstream
// of this hook throws on a bad value — a negative POLL_MS instead makes `wait`
// negative on the healthy path and Math.min(negative, MAX_BACKOFF_MS) negative
// on the backoff path, so setTimeout clamps to 0 and this hook hot-loops the
// CDN at jitter speed, permanently, per open client. An out-of-range POSITIVE
// value (e.g. a fat-fingered extra digit) hits the identical hot-loop failure
// mode via MAX_TIMER_MS overflow instead, so it's rejected the same way.
//
// Number.isInteger, not just Number.isFinite (implies it, so no separate
// check needed): setTimeout truncates a fractional delay to whole
// milliseconds, so e.g. "0.5" would produce a ~1ms REQUEST_TIMEOUT_MS and a
// healthy poll cadence dominated by jitter (~250ms) rather than the intended
// interval — the same hot-loop-against-the-CDN failure mode as an
// out-of-range value, just via truncation instead of overflow. Matches
// cadence.js's readActivityStripTickIntervalMs(), which already requires
// Number.isInteger for the identical reason.
//
// Exported (not just called inline) so tests can exercise every branch
// (unset/invalid/negative/fractional/too-large/valid) directly via an
// injected env object, mirroring cadence.js's readActivityStripTickIntervalMs(env).
export function readPollMs(env: Record<string, string | undefined> = process.env): number {
  const raw = Number(env.REACT_APP_ACTIVITY_STRIP_POLL_MS);
  return Number.isInteger(raw) && raw > 0 && raw <= MAX_POLL_MS ? raw : 5000;
}

const POLL_MS = readPollMs();

// A single poll must not hang indefinitely. Without a bound, a black-holed
// connection (a common enough real-world case on mobile/Capacitor: a carrier
// NAT drop, a captive portal, a stalled CloudFront POP) leaves this effect's
// poll loop dead — no failure recorded, no backoff, no next poll scheduled —
// until a visibility toggle or a Lobby/GuestLobby remount happens to recover
// it, with no bound on how long that takes. Twice the poll interval gives a
// slow-but-alive connection room to answer before this hook gives up on that
// one request and retries with backoff instead, same as any other failure.
const REQUEST_TIMEOUT_MS = POLL_MS * 2;

// Backoff ceiling for a failing read — an outage costs one request per this
// long per open client rather than one per poll interval, without ever
// giving up. Floored at POLL_MS (not just the 30000ms default) — a build
// configured with REACT_APP_ACTIVITY_STRIP_POLL_MS wider than 30s would
// otherwise poll MORE often on failure than when healthy, hammering the
// origin hardest exactly when it's already struggling. This is ONLY the
// backoff ceiling now — freshness/staleness thresholds are derived from the
// document's own published `maxAgeMs` via deriveStaleAfterMs below, not from
// this constant (see that function's comment for why they were split apart).
export const MAX_BACKOFF_MS = 30000;

/**
 * The wait (ms) before the next poll after `failures` consecutive failures
 * (0 = healthy, poll at the normal cadence). Exported as a pure function,
 * separate from the POLL_MS module constant it defaults against, so the
 * floor-at-POLL_MS behavior (see MAX_BACKOFF_MS's own comment) is directly
 * testable against an arbitrary pollMs — re-deriving POLL_MS itself would
 * mean a second module instance built against a different
 * REACT_APP_ACTIVITY_STRIP_POLL_MS, which doesn't mix safely with a single
 * already-loaded React/testing-library instance in the same test file.
 */
export function computeBackoffMs(failures: number, pollMs: number = POLL_MS): number {
  if (failures <= 0) return pollMs;
  const backoffCeilingMs = Math.max(MAX_BACKOFF_MS, pollMs);
  return Math.min(pollMs * 2 ** failures, backoffCeilingMs);
}

// Fallback when a document predates the `maxAgeMs` field (a rolling deploy
// where the CDN is still serving a document written by an older publisher
// build) or the field is present but malformed — matches the publisher's
// own default ACTIVITY_STRIP_TICK_INTERVAL_MS (shared/lib/cadence.js).
export const DEFAULT_MAX_AGE_MS = 5000;

// How many multiples of the publisher's own cadence a document may age
// before it's treated as stale. Not 1x: the publisher's Cache-Control
// max-age is already cadence-minus-1s, and a single missed tick (a transient
// MySQL hiccup, a cold Lambda) shouldn't itself read as an outage — this
// tolerates several missed ticks before giving up, same margin the old fixed
// 30s ceiling gave a 5s-cadence document (30000 / 5000 = 6).
const FRESHNESS_SAFETY_MULTIPLIER = 6;

// Tolerance for the "is this document's generatedAt in the future" check
// below — guards the freshness comparison's OTHER direction. Some skew
// between the responding CloudFront edge's clock and the instant this
// publisher tick actually ran is normal (network/processing latency, not a
// bug); this just keeps that normal slack from being misread as a
// suspiciously-future timestamp.
const CLOCK_SKEW_TOLERANCE_MS = 5000;

// Ceiling on an accepted `maxAgeMs` — must match MAX_TICK_INTERVAL_MS in
// serverless-v2/shared/lib/cadence.js (a separate package/build, so it can't
// be imported directly; same hand-copied-literal convention as DOCUMENT_PATH/
// SUPPORTED_SCHEMA_VERSION above). Without this ceiling, deriveStaleAfterMs
// multiplies whatever the document claims by FRESHNESS_SAFETY_MULTIPLIER with
// no limit — a malformed or tampered document (this is a public,
// unauthenticated document) publishing an extreme `maxAgeMs` would make the
// lobby accept hours- or years-old metrics as fresh instead of ever
// surfacing that the feed had gone stale.
const MAX_ACCEPTED_MAX_AGE_MS = 300000;

// A document's `maxAgeMs` is untrusted input (a public, unauthenticated
// document) — validated the same way readPollMs validates its env var,
// falling back to DEFAULT_MAX_AGE_MS on anything missing/non-numeric/
// non-integer/out-of-range. Number.isInteger (not just Number.isFinite):
// this value only ever means "a whole number of milliseconds" on the
// publisher side (cadence.js requires the same), so a fractional value is
// already malformed input, not a legitimately finer-grained cadence.
function resolveMaxAgeMs(maxAgeMs: unknown): number {
  return typeof maxAgeMs === 'number' &&
    Number.isInteger(maxAgeMs) &&
    maxAgeMs > 0 &&
    maxAgeMs <= MAX_ACCEPTED_MAX_AGE_MS
    ? maxAgeMs
    : DEFAULT_MAX_AGE_MS;
}

// Coerces a document field to a number, or `null` if it isn't really one.
// Only `number` and non-blank `string` are accepted for coercion — anything
// else is rejected outright, rather than handed to the global `Number()`
// coercion, BEFORE that coercion ever runs. `Number(null)` and `Number('')`
// (and `Number('   ')`) both evaluate to `0`, so without the explicit
// null/blank rejection below, isValidCount/isValidAmount would accept a
// malformed document's `null`/blank metric as a plausible-looking zero
// instead of the invalid value it actually is. `Number(true)` and
// `Number(false)` evaluate to `1`/`0` for the same reason — a boolean is not
// a count or a money amount, so it must never reach `Number()` at all; the
// same reasoning excludes arrays/objects, which `Number()` would otherwise
// coerce via their own (often numeric-looking) string conversion.
function numericValue(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string' && value.trim() !== '') {
    const num = Number(value);
    return Number.isFinite(num) ? num : null;
  }
  return null;
}

// A count (tablesActive/playersSeated) must be a finite, non-negative
// integer. Coercing a numeric string is intentional (see hasValidMetrics'
// comment below), but `null`, `''`/whitespace, `NaN`, `Infinity`, a negative
// count, or a fractional value must NOT be accepted as a plausible reading.
function isValidCount(value: unknown): boolean {
  const num = numericValue(value);
  return num !== null && Number.isInteger(num) && num >= 0;
}

// inPlay is a money amount, so unlike a count it's not required to be an
// integer, but it still must be finite and non-negative.
function isValidAmount(value: unknown): boolean {
  const num = numericValue(value);
  return num !== null && num >= 0;
}

/**
 * How old (ms) a document may be, given the cadence it advertises in its own
 * `maxAgeMs` field, before it's treated as stale. Exported so
 * SaloonLobbyContainer's staleness check derives its threshold from the
 * SAME document-published cadence this hook used to decide whether to apply
 * the reading in the first place, rather than importing a hand-copied
 * constant that could silently drift out of sync with a publisher-side
 * cadence change.
 */
export function deriveStaleAfterMs(maxAgeMs: unknown): number {
  return resolveMaxAgeMs(maxAgeMs) * FRESHNESS_SAFETY_MULTIPLIER;
}

// Must match KEY in serverless-v2/services/lobby-broadcast/functions/
// broadcastActivityStrip/activityStripDocument.js (exported there as
// activityStripKey). Exported so the test imports this instead of a second
// hand-copied literal.
export const DOCUMENT_PATH = 'activity-strip/v1/strip.json';

// Must match SCHEMA_VERSION in activityStripDocument.js. This is a public,
// unauthenticated document with no other coordination channel (no auth
// header, no content negotiation), so this is the only lever available to
// reject a document shape this build doesn't understand — e.g. mid-rollout,
// where the publisher and this client build briefly disagree on schema.
// Exported so the test asserts against this instead of a second hand-copied
// literal.
export const SUPPORTED_SCHEMA_VERSION = 1;

export interface ActivityStripFeed {
  metrics: ActivityMetrics;
  /** The publisher's own cadence (ms), as published in the document's `maxAgeMs` field — see deriveStaleAfterMs. */
  maxAgeMs: number;
  /**
   * The DOCUMENT's own `generatedAt` (server truth), not the time this
   * client received it. Callers dispatching this feed into redux (Lobby.tsx,
   * GuestLobby.tsx) must stamp `updatedAt` with THIS value, not `Date.now()`
   * — using receive time would double-count staleness, since this hook has
   * already rejected any document whose generatedAt aged past
   * deriveStaleAfterMs before ever returning it here.
   */
  generatedAtMs: number;
  /**
   * `nowMs - Date.now()` at the moment THIS read was accepted, where `nowMs`
   * is the CloudFront edge's own `Date` response header (falling back to the
   * device clock only when that header is missing/unparseable — see the
   * `nowMs` computation below). Callers that later need to measure elapsed
   * time against `generatedAtMs` (SaloonLobbyContainer's isActivityStale)
   * must add this offset to their own `Date.now()` reading first: comparing
   * a server timestamp straight against a raw, un-corrected device clock
   * reintroduces exactly the fast/slow-clock failure this hook's own
   * isDocumentFresh check was written to avoid (see that check's comment) —
   * a fast device clock would flag a just-published document 'stale'
   * immediately, and a slow one would keep a truly stale document reading as
   * fresh.
   */
  clockOffsetMs: number;
}

/**
 * Returns the most recent metrics (plus the publisher's own cadence they
 * were read at), or `null` before the first successful read. Never reverts
 * to `null` once data has arrived — a stale value beats a blank one.
 *
 * `retrySignal` (default 0) is a plain counter, not a boolean: it is an
 * effect dependency below, so changing its VALUE (not just receiving a
 * truthy one) is what tears down the current in-flight/backed-off poll and
 * fires a fresh one immediately — see the effect's own comment. A caller
 * wanting an on-demand "retry now" affordance (ActivityStrip's Retry action,
 * wired through useActivityStripDispatch/the activityStrip redux slice)
 * increments it on each click; two clicks in a row (e.g. the first retry
 * also fails) must each restart the effect, which a boolean toggled
 * true->true would not.
 */
export function useActivityStripFeed(active: boolean, retrySignal = 0): ActivityStripFeed | null {
  const [feed, setFeed] = useState<ActivityStripFeed | null>(null);

  // Refs, not state: an unchanged document must cost zero reconciliation.
  const versionRef = useRef<number>(0);
  const failuresRef = useRef<number>(0);

  useEffect(() => {
    if (!active) return;

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    // A fresh controller per poll (not one shared for the whole effect) so a
    // per-request timeout can abort just THAT request without tearing down
    // the effect — see REQUEST_TIMEOUT_MS's own comment. `current` is what
    // the effect's cleanup aborts on unmount, whichever request happens to be
    // in flight at that moment.
    let currentController: AbortController | undefined;

    const poll = async () => {
      if (cancelled) return;

      currentController = new AbortController();
      const timeoutId = setTimeout(() => currentController?.abort(), REQUEST_TIMEOUT_MS);

      try {
        // No request headers — see useCdnDocument.ts's identical note: any
        // header outside the CORS safelist forces a preflight, which the
        // distribution cannot answer (S3 has no CORS config, deliberately,
        // to keep Origin out of the cache key).
        const res = await fetch(`${GLOBAL_DATA_CDN_URL}/${DOCUMENT_PATH}`, {
          signal: currentController.signal
        });

        // A response can complete (not just abort) after this effect has
        // already been cancelled — e.g. the tab was hidden and shown again
        // between this fetch starting and resolving. `versionRef`/`failuresRef`
        // are refs shared across effect instances (they don't reset when a
        // new effect starts), so letting a stale response fall through to
        // mutate them below would advance the version watermark for the
        // NEW, current effect without ever calling setFeed for it — silently
        // rejecting a later, correct document at that same version. Checked
        // here (right after every await, before any further processing),
        // not just before setFeed, so no cancelled response can mutate
        // either ref.
        if (cancelled) return;

        if (res.status === 304) {
          // Unconditional reset, unlike the duplicate-version branch below
          // (line ~385) which gates the same reset on isDocumentFresh. That
          // asymmetry is deliberate here, not an oversight: a real 304 has no
          // body, so there is no generatedAt to check freshness against, and
          // this fetch sends no If-None-Match/If-Modified-Since (see the
          // "No request headers" comment above) — CloudFront/S3 have nothing
          // to conditionally-revalidate against, so this branch is not
          // expected to be reachable in practice. Browsers may still surface
          // a cached 304 via the HTTP cache for reasons outside this app's
          // control, so it stays handled defensively rather than removed.
          failuresRef.current = 0;
        } else if (res.ok) {
          const next = await res.json();
          if (cancelled) return;

          if (next?.schemaVersion !== SUPPORTED_SCHEMA_VERSION) {
            // A shape this build doesn't understand — most likely a brief
            // window during a rolling deploy where the publisher and this
            // client are on different schema versions. Not a transient
            // failure (retrying faster can't fix a deploy-coordination gap),
            // so don't feed the backoff — skip this read and keep polling
            // at the normal cadence until the mismatch resolves itself.
            // eslint-disable-next-line no-console
            console.warn(
              `[useActivityStripFeed] ignoring document with schemaVersion ${next?.schemaVersion}, expected ${SUPPORTED_SCHEMA_VERSION}`
            );
            failuresRef.current = 0;
          } else {
            // Computed for EVERY document reaching this point — including a
            // duplicate/older-version one below — not just a newly-applied
            // one. The document's OWN generatedAt (server truth) must be
            // recent — not just present and well-formed. computeActivityStrip
            // returns null on any query failure and broadcastOnce skips the
            // write entirely on a null result (handler.js), so a sustained
            // MySQL problem leaves the CDN serving the LAST GOOD object,
            // untouched, for up to its 1-day S3 lifecycle. Every poll during
            // that window still returns 200 with a valid schemaVersion and
            // the SAME version this reader already applied — a document
            // genuinely well-formed, just stale. Threshold comes from the
            // document's OWN published maxAgeMs (deriveStaleAfterMs), not a
            // fixed constant — see that function's comment.
            //
            // Compared against the CloudFront edge's OWN `Date` response
            // header, not this device's clock. A device clock more than a
            // few seconds fast (common enough on real hardware with a
            // blocked/bad NTP sync — this SPA is Capacitor-wrapped) would
            // otherwise reject every document as stale, forever, with no way
            // to recover; a slow device clock would make `now - generatedAt`
            // go negative and trivially pass, silently disabling this check
            // altogether. Falls back to the device clock only when the
            // header is missing or unparseable — some fetch polyfills/test
            // environments don't expose it.
            const responseDateMs = Date.parse(res.headers.get('date') ?? '');
            const nowMs = Number.isFinite(responseDateMs) ? responseDateMs : Date.now();
            const generatedAtMs = Date.parse(next?.generatedAt);
            const staleAfterMs = deriveStaleAfterMs(next?.maxAgeMs);
            const isDocumentFresh =
              Number.isFinite(generatedAtMs) &&
              generatedAtMs <= nowMs + CLOCK_SKEW_TOLERANCE_MS &&
              nowMs - generatedAtMs <= staleAfterMs;

            if (typeof next?.version === 'number' && next.version <= versionRef.current) {
              // A duplicate (or older) version — the same underlying signal
              // as the "well-formed but stale" branch below, just reached
              // via a different path: an outage frozen at the version this
              // reader already applied looks IDENTICAL, poll after poll, to
              // "nothing new yet," except that `isDocumentFresh` keeps
              // getting false the longer it goes on. Without this check, a
              // publisher outage that started AFTER this reader's last good
              // read would reset failuresRef to 0 on every single poll
              // forever — never backing off, and masking exactly the outage
              // this backoff path exists to signal.
              failuresRef.current = isDocumentFresh ? 0 : failuresRef.current + 1;
            } else {
              // All three fields must be present AND hold a plausible value,
              // not just be non-undefined: a document missing playersSeated/
              // inPlay would otherwise coerce via `Number(undefined) || 0`
              // into a plausible-looking but wrong zero reading, and a
              // malformed/tampered document (negative counts, `NaN`,
              // `Infinity`, a non-numeric string) would render an impossible
              // activity reading instead of being skipped. Not gated on
              // typeof === 'number': isValidCount/isValidAmount already
              // coerce a numeric string safely, and a stricter gate here
              // would just silently drop an otherwise-valid document.
              const hasValidMetrics =
                next?.tablesActive !== undefined &&
                isValidCount(next.tablesActive) &&
                next?.playersSeated !== undefined &&
                isValidCount(next.playersSeated) &&
                next?.inPlay !== undefined &&
                isValidAmount(next.inPlay);
              // version itself must be a real number before it's trusted as
              // a watermark: the `typeof === 'number'` check above only
              // filters a numeric version, so a non-numeric one (a malformed
              // document, e.g. version sent as a string or null) falls
              // through to here. Assigning it to versionRef.current — a
              // number ref — would corrupt every future staleness
              // comparison.
              const hasValidVersion =
                typeof next?.version === 'number' && Number.isFinite(next.version);

              // versionRef only advances when the document is actually
              // applied — a malformed OR stale document at version N must
              // not block a later, corrected/fresh document at that same
              // version from getting through.
              if (hasValidMetrics && hasValidVersion && isDocumentFresh) {
                versionRef.current = next.version;
                if (!cancelled) {
                  setFeed({
                    metrics: {
                      tablesActive: Number(next.tablesActive) || 0,
                      playersSeated: Number(next.playersSeated) || 0,
                      inPlay: Number(next.inPlay) || 0
                    },
                    maxAgeMs: resolveMaxAgeMs(next?.maxAgeMs),
                    generatedAtMs,
                    clockOffsetMs: nowMs - Date.now()
                  });
                }
                failuresRef.current = 0;
              } else if (hasValidMetrics && hasValidVersion && !isDocumentFresh) {
                // Well-formed but stale: unlike the other malformed-document
                // cases above, this DOES feed the backoff — the server has
                // stopped producing new data, and retrying less often
                // (rather than resetting to the normal cadence) is the
                // right response, same as any other real failure. This also
                // means dispatches stop, so SaloonLobbyContainer's own
                // updatedAt staleness check takes over and hides the strip
                // once this has gone on long enough, instead of confidently
                // showing hours-old numbers.
                failuresRef.current += 1;
              } else {
                failuresRef.current = 0;
              }
            }
          }
        } else if ((res.status === 404 || res.status === 403) && versionRef.current === 0) {
          // Not written yet — the publisher writes on its next tick after
          // deploy. Not a failure; keep polling at the normal rate.
          //
          // 403, not just 404: the bucket policy (serverless.yml) grants
          // CloudFront only s3:GetObject, no s3:ListBucket — without list
          // permission, S3/CloudFront cannot distinguish "object doesn't
          // exist" from "exists but access is denied", so it answers 403
          // AccessDenied for a missing key instead of 404. Confirmed against
          // a live distribution. serverless.yml's CustomErrorResponses
          // already anticipates this (a 403 entry sits right next to the 404
          // one) — this branch was the one place that hadn't caught up, so a
          // pre-first-publish read escalated into full exponential backoff
          // instead of the steady retry this branch exists for.
          //
          // Gated on versionRef.current === 0 (no document ever successfully
          // applied yet): the publisher republishes this exact key every
          // tick, so once a version HAS been read, a later 404/403 is not
          // "hasn't published yet" anymore — the object (or this reader's
          // access to it) actually went away, e.g. a bucket policy,
          // distribution, or IAM regression. Genuine S3 "doesn't exist" and
          // "access denied" are still indistinguishable from the response
          // alone (see above), but "used to exist, now doesn't/can't-read" is
          // a real signal this reader does have. Without this gate, that
          // failure mode polled forever at the normal cadence with no
          // backoff and no client-side signal an operator could see.
          failuresRef.current = 0;
        } else {
          failuresRef.current += 1;
        }
      } catch (err) {
        // An AbortError while `cancelled` is true is this effect's own
        // cleanup (unmount) aborting an in-flight request — not a failure,
        // and there's nothing to reschedule since the effect is tearing
        // down. An AbortError while `cancelled` is still false is
        // REQUEST_TIMEOUT_MS's own timer firing — a real failure (a
        // black-holed request) that must feed the backoff and reschedule
        // like any other, not be silently dropped the way this branch used
        // to treat every AbortError.
        if ((err as any)?.name === 'AbortError' && cancelled) return;
        failuresRef.current += 1;
      } finally {
        clearTimeout(timeoutId);
      }

      if (cancelled) return;

      const wait = computeBackoffMs(failuresRef.current);

      // Jitter so clients polling on the same tick don't spike the origin together.
      timer = setTimeout(poll, wait + Math.random() * 500);
    };

    poll();

    return () => {
      cancelled = true;
      currentController?.abort();
      if (timer) clearTimeout(timer);
    };
    // retrySignal is intentionally a dependency purely to restart this
    // effect on change (cleanup aborts/cancels whatever poll was in flight
    // or scheduled, then `poll()` above fires immediately) — its VALUE is
    // never read inside the effect body, only its identity across renders.
  }, [active, retrySignal]);

  return feed;
}
